import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { TextFieldSearchFilter } from "@/shared/components/TextFieldSearchFilter";
import { SurfaceDefect } from "@/shared/models/Inspection";
import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import { Grid2, Paper, Typography } from "@mui/material";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useState } from "react";
import { useSurfaceDefectOptionsFunctions } from "../hooks/useSurfaceDefectOptionsFunctions";
import { useTableData } from "../hooks/useTableData";
import { useSurfaceDefectCatalog } from "../SurfaceDefectsPage";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

export const TableSurfaceDefects = () => {
	const [filterValue, setFilterValue] = useState("");
	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [showModal, setShowModal] = useState<
		"create" | "update" | "delete" | ""
	>("");
	const [selectedRowData, setSelectedRowData] = useState<SurfaceDefect | null>(
		null,
	);
	const [error, setError] = useState("");

	const { data: rows, isLoading } = useSurfaceDefectCatalog((queryError) =>
		setError(queryError.message),
	);

	const handleNewSurfaceDefect = () => {
		if (gridApi) gridApi.deselectAll();
		setSelectedRowData(null);
		setShowModal("create");
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { getMenuOptions } = useSurfaceDefectOptionsFunctions({
		setSelectedRowData,
		setShowModal,
		handleNewSurfaceDefect,
	});
	const { columnDefs } = useTableData({
		setSelectedRowData,
		setShowModal,
		showContextMenu,
	});
	const { defaultColDef } = useGridDefinitions({});

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const data = params.node?.data;
			showContextMenu(event as any, data, "surfaceDefectContext");
			if (data) {
				params.api.deselectAll();
				params.node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	useEffect(() => {
		registerConfig({
			id: "surfaceDefectContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<Grid2 container justifyContent="center" p={1} spacing={1}>
			<Grid2 size={{ md: 12, xs: 12 }}>
				<Typography variant="h5" fontWeight={600}>
					Surface Defects
				</Typography>
				<Typography variant="body2" color="textSecondary">
					The defects an inspection reading can be marked with.
				</Typography>
			</Grid2>
			<Grid2 size={{ xs: 12, md: 12 }}>
				<TextFieldSearchFilter
					value={filterValue}
					onChange={(value) => {
						setFilterValue(value);
						gridApi?.setGridOption("quickFilterText", value);
					}}
				/>
			</Grid2>
			<Grid2 container justifyContent="center" alignItems="center">
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper elevation={2} style={{ height: "64vh", width: "100%" }}>
						<AgGridReact<SurfaceDefect>
							loading={isLoading}
							rowData={rows || []}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={34}
							animateRows
							getContextMenuItems={getContextMenuItems}
							onRowDoubleClicked={(event) => {
								setSelectedRowData(event.data as SurfaceDefect);
								setShowModal("update");
							}}
							onGridReady={(params) => setGridApi(params.api)}
							getRowId={(params) => `${params.data.SurfaceDefectId}`}
							loadingOverlayComponent={CenteredLazyLoading}
							gridOptions={{
								rowSelection: {
									mode: "singleRow",
									checkboxes: false,
									enableClickSelection: true,
								},
							}}
						/>
					</Paper>
				</Grid2>
				<FooterButton
					label="SURFACE DEFECT"
					dataTestId="new-surface-defect-button"
					showCreateModal={showModal === "create"}
					setShowCreateModal={(show) =>
						show ? handleNewSurfaceDefect() : setShowModal("")
					}
					sizes={{ md: 3 }}
				/>
			</Grid2>
			<UpsertDeleteModal
				surfaceDefect={selectedRowData}
				modal={showModal}
				onHide={() => setShowModal("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</Grid2>
	);
};
